import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'

/**
 * Clarification copy for the local spec-intent planner — F7's authored home.
 *
 * What was here before: no module at all. Each clarification was a pair of
 * literals passed to
 *
 *   function clarification(content, english, korean) {
 *     return { text: /[가-힣]/.test(content) ? korean : english }
 *   }
 *
 * — so the LANGUAGE of the reply was chosen by looking for Hangul in the
 * USER'S OWN MESSAGE. Measured consequences, both real:
 *
 *   • `lang="en"` session, request `Change the intro headline to 한도 조회 안내`
 *     (English prose quoting one Korean string) → an entirely Korean answer.
 *   • `lang="ja"` session, any request containing Hangul → a KOREAN answer.
 *     The sweep predicted `ja`/`zh`/`zh-TW` would "always get English"; they
 *     get English only while the user types no Hangul.
 *
 * A Korean user writing an English request is not asking for an English UI,
 * and there is no rule about the content of a message that can substitute for
 * asking the locale. So the language comes from the locale, and the five
 * languages are all written out here rather than two of them being reachable.
 */
export type SpecIntentClarificationKey =
  | 'document-page-title'
  | 'remove-element'
  | 'update-element'
  | 'component-type'
  | 'variable-name'
  | 'variable-type'
  | 'datasource-name'
  | 'datasource-url-or-method'
  | 'datasource-endpoint'
  | 'unbind-element'
  | 'unbind-event-type'
  | 'flow-rename-select'
  | 'flow-rename-name'
  | 'flow-delete-select'
  | 'flow-create-name'
  | 'text-to-add'
  | 'text-to-update'
  | 'document-page-prepared'

/** `term` — a component word the user used; `name` — a variable/datasource. */
export interface SpecIntentClarificationParams {
  term?: string
  name?: string
  title?: string
}

type ClarificationTable = Record<
  SpecIntentClarificationKey,
  (params: SpecIntentClarificationParams) => string
>

export const SPEC_INTENT_CLARIFICATION_COPY: Record<
  LocaleCode,
  ClarificationTable
> = {
  en: {
    'document-page-title': () =>
      'Tell me the exact document page title before I create it.',
    'remove-element': () => 'Select the exact element to remove, then ask again.',
    'update-element': () => 'Select the exact element to update, then ask again.',
    'component-type': ({ term }) =>
      `Choose the exact component type for "${term}" before I add it.`,
    'variable-name': () =>
      'Tell me the exact variable name before I change Studio state.',
    'variable-type': ({ name }) =>
      `Tell me the exact type for variable "${name}" (string, number, boolean, array, or object).`,
    'datasource-name': () =>
      'Tell me the exact datasource name before I change Studio data configuration.',
    'datasource-url-or-method': ({ name }) =>
      `Tell me the exact URL or HTTP method to change on datasource "${name}".`,
    'datasource-endpoint': ({ name }) =>
      `Tell me the exact endpoint URL for datasource "${name}".`,
    'unbind-element': () =>
      'Select the exact element whose event binding should be removed.',
    'unbind-event-type': () =>
      'Tell me the exact event type to unbind (for example click, change, or submit).',
    'flow-rename-select': () =>
      'Select the exact ActionFlow to rename, then ask again.',
    'flow-rename-name': () => 'Tell me the exact new ActionFlow name.',
    'flow-delete-select': () =>
      'Select the exact ActionFlow to delete, then ask again.',
    'flow-create-name': () =>
      'Tell me the exact ActionFlow name before I create it.',
    'text-to-add': () =>
      'What text should I add? Send the exact words in quotes, for example: Add "Welcome".',
    'text-to-update': () =>
      'What should the text say? Send the exact words in quotes, for example: Change the title to "Welcome".',
    'document-page-prepared': ({ title }) =>
      `Prepared document page "${title}". Review the change before applying it.`,
  },
  ko: {
    'document-page-title': () =>
      '문서 페이지를 추가하기 전에 정확한 페이지 제목을 알려주세요.',
    'remove-element': () => '삭제할 정확한 요소를 선택한 뒤 다시 요청해주세요.',
    'update-element': () => '수정할 정확한 요소를 선택한 뒤 다시 요청해주세요.',
    'component-type': ({ term }) =>
      `"${term}"에 해당하는 정확한 컴포넌트 유형을 선택해주세요.`,
    'variable-name': () =>
      'Studio 상태를 변경하기 전에 정확한 변수 이름을 알려주세요.',
    'variable-type': ({ name }) =>
      `변수 "${name}"의 정확한 타입(string, number, boolean, array, object)을 알려주세요.`,
    'datasource-name': () =>
      'Studio 데이터 설정을 변경하기 전에 정확한 데이터소스 이름을 알려주세요.',
    'datasource-url-or-method': ({ name }) =>
      `데이터소스 "${name}"에서 변경할 정확한 URL 또는 HTTP method를 알려주세요.`,
    'datasource-endpoint': ({ name }) =>
      `데이터소스 "${name}"의 정확한 endpoint URL을 알려주세요.`,
    'unbind-element': () => '이벤트 바인딩을 제거할 정확한 요소를 선택해주세요.',
    'unbind-event-type': () =>
      '해제할 정확한 이벤트 타입(예: click, change, submit)을 알려주세요.',
    'flow-rename-select': () =>
      '이름을 변경할 정확한 ActionFlow를 선택한 뒤 다시 요청해주세요.',
    'flow-rename-name': () => '새 ActionFlow 이름을 정확히 알려주세요.',
    'flow-delete-select': () =>
      '삭제할 정확한 ActionFlow를 선택한 뒤 다시 요청해주세요.',
    'flow-create-name': () =>
      'ActionFlow를 생성하기 전에 정확한 이름을 알려주세요.',
    'text-to-add': () =>
      '추가할 문구를 따옴표로 감싸서 알려주세요. 예: 추가 "환영합니다".',
    'text-to-update': () =>
      '어떤 문구로 바꿀지 따옴표로 감싸서 알려주세요. 예: 제목을 "환영합니다"로 변경.',
    'document-page-prepared': ({ title }) =>
      `"${title}" 문서 페이지를 추가할 준비를 했습니다. 변경 내용을 확인해 주세요.`,
  },
  ja: {
    'document-page-title': () =>
      'ドキュメントページを追加する前に、正確なページタイトルを教えてください。',
    'remove-element': () =>
      '削除する要素を正確に選んでから、もう一度お知らせください。',
    'update-element': () =>
      '変更する要素を正確に選んでから、もう一度お知らせください。',
    'component-type': ({ term }) =>
      `「${term}」に当たるコンポーネントの種類を正確に選んでください。`,
    'variable-name': () =>
      'Studio の状態を変更する前に、正確な変数名を教えてください。',
    'variable-type': ({ name }) =>
      `変数「${name}」の正確な型（string、number、boolean、array、object）を教えてください。`,
    'datasource-name': () =>
      'Studio のデータ設定を変更する前に、正確なデータソース名を教えてください。',
    'datasource-url-or-method': ({ name }) =>
      `データソース「${name}」で変更する URL または HTTP メソッドを正確に教えてください。`,
    'datasource-endpoint': ({ name }) =>
      `データソース「${name}」の正確なエンドポイント URL を教えてください。`,
    'unbind-element': () =>
      'イベントの結び付けを外す要素を正確に選んでください。',
    'unbind-event-type': () =>
      '解除するイベントの種類（例：click、change、submit）を正確に教えてください。',
    'flow-rename-select': () =>
      '名前を変える ActionFlow を正確に選んでから、もう一度お知らせください。',
    'flow-rename-name': () => '新しい ActionFlow の名前を正確に教えてください。',
    'flow-delete-select': () =>
      '削除する ActionFlow を正確に選んでから、もう一度お知らせください。',
    'flow-create-name': () =>
      'ActionFlow を作る前に、正確な名前を教えてください。',
    'text-to-add': () =>
      '追加する文言をかぎかっこで囲んで教えてください。例:「ようこそ」を追加。',
    'text-to-update': () =>
      'どの文言に変えるかをかぎかっこで囲んで教えてください。例: タイトルを「ようこそ」に変更。',
    'document-page-prepared': ({ title }) =>
      `「${title}」ドキュメントページを追加する準備をしました。変更内容をご確認ください。`,
  },
  zh: {
    'document-page-title': () => '创建文档页面前，请告诉我准确的页面标题。',
    'remove-element': () => '请先准确选中要删除的元素，然后再说一次。',
    'update-element': () => '请先准确选中要修改的元素，然后再说一次。',
    'component-type': ({ term }) => `请为「${term}」选择准确的组件类型，然后我再添加。`,
    'variable-name': () => '更改 Studio 状态前，请告诉我准确的变量名。',
    'variable-type': ({ name }) =>
      `请告诉我变量「${name}」的准确类型（string、number、boolean、array 或 object）。`,
    'datasource-name': () => '更改 Studio 数据配置前，请告诉我准确的数据源名称。',
    'datasource-url-or-method': ({ name }) =>
      `请告诉我要在数据源「${name}」上更改的准确 URL 或 HTTP 方法。`,
    'datasource-endpoint': ({ name }) =>
      `请告诉我数据源「${name}」的准确 endpoint URL。`,
    'unbind-element': () => '请准确选中要解除事件绑定的元素。',
    'unbind-event-type': () =>
      '请告诉我要解除的准确事件类型（例如 click、change 或 submit）。',
    'flow-rename-select': () => '请先准确选中要改名的 ActionFlow，然后再说一次。',
    'flow-rename-name': () => '请告诉我新的 ActionFlow 名称。',
    'flow-delete-select': () => '请先准确选中要删除的 ActionFlow，然后再说一次。',
    'flow-create-name': () => '创建前，请告诉我准确的 ActionFlow 名称。',
    'text-to-add': () =>
      '请把要添加的文字放在引号里告诉我。例如：添加“欢迎”。',
    'text-to-update': () =>
      '请把要改成的文字放在引号里告诉我。例如：把标题改成“欢迎”。',
    'document-page-prepared': ({ title }) =>
      `已准备好添加文档页面「${title}」。请先确认这项更改。`,
  },
  'zh-TW': {
    'document-page-title': () => '建立文件頁面前，請告訴我準確的頁面標題。',
    'remove-element': () => '請先準確選取要刪除的元素，然後再說一次。',
    'update-element': () => '請先準確選取要修改的元素，然後再說一次。',
    'component-type': ({ term }) => `請為「${term}」選擇準確的元件類型，然後我再新增。`,
    'variable-name': () => '變更 Studio 狀態前，請告訴我準確的變數名稱。',
    'variable-type': ({ name }) =>
      `請告訴我變數「${name}」的準確型別（string、number、boolean、array 或 object）。`,
    'datasource-name': () => '變更 Studio 資料設定前，請告訴我準確的資料來源名稱。',
    'datasource-url-or-method': ({ name }) =>
      `請告訴我要在資料來源「${name}」上變更的準確 URL 或 HTTP 方法。`,
    'datasource-endpoint': ({ name }) =>
      `請告訴我資料來源「${name}」的準確 endpoint URL。`,
    'unbind-element': () => '請準確選取要解除事件綁定的元素。',
    'unbind-event-type': () =>
      '請告訴我要解除的準確事件類型（例如 click、change 或 submit）。',
    'flow-rename-select': () => '請先準確選取要改名的 ActionFlow，然後再說一次。',
    'flow-rename-name': () => '請告訴我新的 ActionFlow 名稱。',
    'flow-delete-select': () => '請先準確選取要刪除的 ActionFlow，然後再說一次。',
    'flow-create-name': () => '建立前，請告訴我準確的 ActionFlow 名稱。',
    'text-to-add': () =>
      '請把要新增的文字放在引號裡告訴我。例如：新增「歡迎」。',
    'text-to-update': () =>
      '請把要改成的文字放在引號裡告訴我。例如：把標題改成「歡迎」。',
    'document-page-prepared': ({ title }) =>
      `已準備好新增文件頁面「${title}」。請先確認這項變更。`,
  },
}

export function specIntentClarificationText(
  key: SpecIntentClarificationKey,
  locale: LocaleCode,
  params: SpecIntentClarificationParams = {},
): string {
  const table =
    SPEC_INTENT_CLARIFICATION_COPY[locale] ?? SPEC_INTENT_CLARIFICATION_COPY.en
  return table[key](params)
}
